import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ChevronRight, Medal, SlidersHorizontal, Trophy, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import type { GameCode, Room } from '../../entities/room/types';
import {
  fetchLeaderboard,
  fetchPublicProfile,
  followUser,
  unfollowUser,
  type LeaderboardBoard,
  type LeaderboardGame,
  type LeaderboardItem,
  type LeaderboardLastBet,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type LeaderboardSort,
  type LeaderboardUser,
  type PublicProfileBet,
  type PublicProfilePeriod,
  type PublicProfileResponse,
} from '../../features/leaderboard/api';
import { fetchRooms } from '../../features/lobby/api';
import { bigWinWhenLabel, daysAgo, gameLabelKey, multipleLabel } from './bigWinsView';
import { inProfitOnly, longerPeriod } from './profitBoardView';
import { useAuthStore } from '../../features/auth/authStore';
import { useCountryStore } from '../../shared/country/countryStore';
import { GAME_LABELS, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { SheetPortal } from '../../shared/ui/SheetPortal';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtMoney, fmtInt } from '../../shared/lib/format';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import { RoundDetailSheet } from '../../features/milhar/RoundDetailSheet';
import { useHistoryLayers } from '../../shared/navigation/useHistoryLayers';
import type { MilharTierId } from '../../shared/milhar/tiers';
import {
  PublicProfileSheet,
  fmtSignedBRL,
  fmtSignedPct,
  leaderboardAvatarUrl,
  periodLabel,
  playsLabel,
  profileApiUserId,
  profilePeriodFor,
  profitTone,
  type RoomNames,
  type UiLeaderboardItem,
} from '../../features/profile/PublicProfileSheet';

// Short-term-focused: spotting a lucky streak wants recent windows, not all-day
// totals. "today" is the longest we surface (per product direction).
const PERIODS: LeaderboardPeriod[] = ['30m', '1h', '3h', 'today'];
const GAMES: LeaderboardGame[] = ['all', 'lastdigit', 'sicbo', 'roulette'];
const SORTS: LeaderboardSort[] = ['profit', 'roi', 'streak'];
// 中大奖是低频事件：短窗口下这个榜会和它替换掉的 Who copied 一样天天是空的。
const WIN_PERIODS: LeaderboardPeriod[] = ['today', '7d', '30d'];
// 去掉抽奖之后，付费玩法一周只有十几条中奖、一个月六十来条。7 天窗口撑不起一屏，
// 而这个榜的问题从来不是「太长」，是「太空」。
const WIN_DEFAULT_PERIOD: LeaderboardPeriod = '30d';
// Draw 不在这里 —— 抽奖占了这个榜 99.4% 的行，而且清一色 R$ 20 / R$ 50 的免费票
// 奖金，把付费玩法那十几条真正的大奖冲得看不见（BIG_WINS_BOARD_DESIGN §2.3）。
const WIN_GAMES: LeaderboardGame[] = ['all', 'milhar', 'milhar-hora'];
// A board built on one or two bets is noise dressed as a ranking. The server
// defaults to 5; these are the only other honest options we offer.
const MIN_PLAYS_OPTIONS = [1, 3, 5, 10];

export function LeaderboardPage() {
  const token = useAuthStore((state) => state.token);
  const activeProfileUserId = useAppStore((state) => state.activeProfileUserId);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  // Default to the shortest window: "who's hot recently" beats a stale all-day
  // total for spotting a lucky streak. True 30m/10-round windows need backend
  // aggregations (recorded in todo.md) — 1h is the shortest the API supports.
  // Big wins opens first (2026-09-04): the paid-out prizes are the pull, the
  // profit ranking is the deeper cut. Its default window is the wins window.
  const [period, setPeriod] = useState<LeaderboardPeriod>(WIN_DEFAULT_PERIOD);
  const [game, setGame] = useState<LeaderboardGame>('all');
  const [roomId, setRoomId] = useState('all');
  const [board, setBoard] = useState<LeaderboardBoard>('wins');
  const [sort, setSort] = useState<LeaderboardSort>('profit');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [minPlays, setMinPlays] = useState(5);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<UiLeaderboardItem | null>(null);
  // Milhar 走已有的开奖详情弹窗；Draw 走已有的抽奖结果弹窗。都不新造页面 ——
  // 那两个页面已经是公开、完整的记录（连没中的人也列着），可信度正来自于此。
  const [winRound, setWinRound] = useState<{ round: number; tier: string } | null>(null);
  // 访客点 Draw 行拿不到结果（后端结果查询要 token）——产品裁决是弹已有的
  // 访客登录墙，而不是把访客悄悄导去一个会失败的查询（BIG_WINS_BOARD_BACKEND_TODO.md
  // §8.1）。Milhar 不受影响：那个端点的 Authorization 头是可选的。
  // 中大奖榜点进去的开奖详情是整屏浮层——不挂进历史的话，系统返回键关掉的
  // 不是这一层，是整个排行榜（commit 12b39df8 定的规矩，见 LobbyPage 的用法）。
  useHistoryLayers([
    { id: 'leaderboard.winround', open: !!winRound, onClose: () => setWinRound(null) },
  ]);
  const t = useI18nStore((state) => state.t);
  const setRoute = useAppStore((state) => state.setRoute);
  const showToast = useAppStore((state) => state.showToast);
  const setActiveRoomId = useAppStore((state) => state.setActiveRoomId);
  const profileReturn = useAppStore((state) => state.profileReturn);
  const setProfileReturn = useAppStore((state) => state.setProfileReturn);
  const reopenRaffleSheetAt = useAppStore((state) => state.reopenRaffleSheetAt);
  const country = useCountryStore((state) => state.country);
  useI18nStore((state) => state.language);

  const roomsQuery = useQuery({
    queryKey: ['rooms'],
    queryFn: fetchRooms,
  });
  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', token || 'guest', period, game, roomId, board, sort, onlineOnly, minPlays],
    queryFn: () => fetchLeaderboard({ token, period, game, room: roomId, board, sort, onlineOnly, minPlays }),
    // Every window here is a *rolling* one — the server's `since` advances with
    // the clock — and nothing else on this screen refetches: window focus is
    // off globally and react-query only refetches on mount or a key change. So
    // the board froze at whatever it read when you arrived, while opening a
    // profile issued a fresh query. Leave the page open a few minutes and the
    // card said +R$ 4.650 while the sheet behind it said +R$ 3.225 — the same
    // number, minutes apart, with one big win having aged out of the window in
    // between. Polling bounds that gap to one interval.
    refetchInterval: 30_000,
  });

  const rooms = roomsQuery.data?.rooms || [];
  const roomOptions = useMemo(() => rooms.filter((room) => game === 'all' || room.game === game), [game, rooms]);
  const roomNames = useMemo(() => roomLabelMap(rooms, t), [rooms, t]);
  // A server that does not know `board` happily answers with the profit board.
  // Rendering those rows under "Big wins" would put a player's hourly profit
  // where a single win's payout belongs — numbers that mean something else
  // entirely — so an unacknowledged board shows as empty.
  const boardAnswered = (leaderboardQuery.data?.board || 'profit') === board;
  // Same guard for the ranking metric: a server that ignored `sort` returned
  // profit order, so the row must show profit — not an ROI column of zeroes.
  const effectiveSort: LeaderboardSort = leaderboardQuery.data?.sort || 'profit';
  const rankedRows = useMemo(
    () => (boardAnswered ? normalizeLeaderboard(leaderboardQuery.data?.items || []) : []),
    [leaderboardQuery.data?.items, boardAnswered]
  );
  // The profit board lists winners only — see profitBoardView. The wins board
  // is already a list of payouts, nothing to hide there.
  const rows = useMemo(
    () => (board === 'profit' ? inProfitOnly(rankedRows) : rankedRows),
    [rankedRows, board]
  );
  // The server had ranked players, but none of them is up: a different empty
  // state from "no bets yet", with a way out (a longer window).
  const nobodyInProfit = board === 'profit' && rankedRows.length > 0 && rows.length === 0;
  const nextPeriod = longerPeriod(period);
  // 每次渲染取一次，整张列表共用（见列表处的说明）。
  const nowSeconds = Math.floor(Date.now() / 1000);
  const myRow = useMemo(() => {
    const me = leaderboardQuery.data?.me;
    return me ? normalizeLeaderboard([me])[0] : null;
  }, [leaderboardQuery.data?.me]);
  // Only the server can say which threshold it actually applied. Printing our
  // requested value against a backend that ignored it would put a claim on
  // screen the list visibly contradicts.
  const appliedMinPlays = Number(leaderboardQuery.data?.minPlays || 0);
  // The podium is an award ceremony. Handing out medals for a losing hour is
  // exactly how the board lost its credibility, so it only appears when the
  // leader is genuinely up — and only under the metric it ranks by.
  const showPodium = board === 'profit' && effectiveSort === 'profit' && rows.length >= 3 && rows[0].profit > 0;
  const podium = showPodium ? [rows[1], rows[0], rows[2]].filter(Boolean) : [];
  const rest = showPodium ? rows.slice(3) : rows;
  const activeFilterCount = (game === 'all' ? 0 : 1) + (roomId === 'all' ? 0 : 1)
    + (sort === 'profit' ? 0 : 1) + (minPlays === 5 ? 0 : 1);

  useEffect(() => {
    if (!activeProfileUserId) return;
    const match = findLeaderboardProfile(rows, activeProfileUserId);
    setProfileUser((current) => {
      if (current && profileRouteId(current) === activeProfileUserId && !match) return current;
      return match || routeProfileFallback(activeProfileUserId);
    });
  }, [activeProfileUserId, rows]);

  function changeGame(next: LeaderboardGame) {
    setGame(next);
    if (next !== 'all') {
      const selected = rooms.find((room) => room.id === roomId);
      if (selected && selected.game !== next) setRoomId('all');
    }
  }

  // 卡片点进去打开的是头奖那一期的开奖详情 —— 和榜单行走同一条路，
  // 玩家能在那里看到那一期每一个人的选号，包括没中的。
  function openJackpot(row: LeaderboardResponse['lastJackpot']) {
    if (!row || typeof row.roundNumber !== 'number') return;
    const tier = row.game === 'milhar-hora' ? '5' : row.game === 'milhar' ? '4' : null;
    if (!tier) return;
    setWinRound({ round: row.roundNumber, tier });
  }

  function openWin(row: UiLeaderboardItem) {
    if (typeof row.roundNumber !== 'number') return;
    // row.tier 不可信（可能缺失或空），且默认成 '4' 会把一条 milhar-hora 的中奖
    // 打开成 4 位数榜的某一期——真实存在的一期，不同的号码，不同的中奖人，
    // 没有任何报错。tier 从 row.game 推导，推不出来就不开，不瞎猜。
    const tier = row.game === 'milhar-hora' ? '5' : row.game === 'milhar' ? '4' : null;
    if (!tier) return;
    setWinRound({ round: row.roundNumber, tier });
  }

  function openProfile(item: UiLeaderboardItem) {
    setProfileUser(item);
    setActiveProfileUserId(profileRouteId(item));
  }

  function closeProfile() {
    setProfileUser(null);
    setActiveProfileUserId(null);
    // Opened from a raffle sheet → return there instead of stranding the user
    // on this leaderboard page. The sheet reopens over the page it was on.
    if (profileReturn) {
      const ret = profileReturn;
      setProfileReturn(null);
      if (ret.view) {
        reopenRaffleSheetAt(ret.origin ?? { route: 'sorteios', roomId: null }, ret.view, ret.resultId);
      } else {
        // Plain page origin (e.g. the follows list, a room's bet feed) — no
        // sheet to reopen. A room origin has to hand its id back first:
        // routing away from 'room' nulls activeRoomId, so returning without it
        // lands on a room page with no room.
        if (ret.origin?.route === 'room' && ret.origin.roomId) setActiveRoomId(ret.origin.roomId);
        setRoute(ret.origin?.route || 'lobby', { replace: true });
      }
    }
  }

  const empty = !leaderboardQuery.isLoading && !leaderboardQuery.isError && rows.length === 0;

  return (
    <section className="tab-page leaderboard-page">
      <header className="tab-page-header">
        <button className="icon-button" type="button" onClick={() => setRoute('lobby')} aria-label={t('backToLobby')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div>
          <p className="eyebrow">{t('topEyebrow')}</p>
          <h1>{t('leaderboardTitle')}</h1>
        </div>
      </header>

      <section className="leaderboard-controls">
        {/* Two boards, two different questions: who is doing well lately, and
            which single bets just paid big. Ranking people twice would make the
            second tab a slower copy of the first (BIG_WINS_BOARD_DESIGN §1). */}
        <div className="leaderboard-boards" role="tablist" aria-label={t('leaderboardBoardLabel')}>
          {(['wins', 'profit'] as LeaderboardBoard[]).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={board === item}
              className={board === item ? 'active' : ''}
              onClick={() => {
                setBoard(item);
                // 两个榜的窗口集合不相交（1h 在 wins 下不存在，30d 在 profit 下无意义）。
                // 不夹一下就会带着一个对方不认识的 period 发请求。
                const nextPeriods = item === 'wins' ? WIN_PERIODS : PERIODS;
                if (!nextPeriods.includes(period)) setPeriod(item === 'wins' ? WIN_DEFAULT_PERIOD : '1h');
                // 同样的坑在 game 上：GAMES 和 WIN_GAMES 除 'all' 外互不相交，
                // 不夹住就会带着 wins 或 profit 读不懂的 game 发请求，
                // 榜单会静默变空或报错，玩家只会看到一个不明所以的 Filters 徽章。
                const nextGames = item === 'wins' ? WIN_GAMES : GAMES;
                if (!nextGames.includes(game)) changeGame('all');
                // room 挑的是 lastdigit/sicbo/roulette 房间；milhar/milhar-hora/raffle
                // 没有房间概念，进 wins 时把 room 也夹回 'all'，否则一个残留的房间 id
                // 会被当成 wins 读不懂的过滤条件发出去（changeGame 的房间重置只在
                // next !== 'all' 时触发，管不到这一步）。
                if (item === 'wins' && roomId !== 'all') setRoomId('all');
                // 同一个坑，另外两个把手：wins 榜不露 Playing now 开关和 Filters
                // 按钮，但两者仍会跟着 fetchLeaderboard 一起发出去。留着上一个榜的
                // 值，玩家在 Top players 开了 Playing now 再切到 Big wins，看到的
                // 就是一个悄悄被过滤空了的榜——开关不在画面上，没法关，也不知道
                // 为什么空。跟 roomId 一样，进 wins 时夹回默认值。
                if (item === 'wins') {
                  if (onlineOnly) setOnlineOnly(false);
                  if (minPlays !== 5) setMinPlays(5);
                }
              }}
            >
              {item === 'profit' ? t('leaderboardBoardProfit') : t('leaderboardBoardWins')}
            </button>
          ))}
        </div>

        {board === 'wins' ? (
          <div className="leaderboard-games" role="tablist" aria-label={t('leaderboardFilters')}>
            {WIN_GAMES.map((item) => (
              <button key={item} type="button" role="tab" aria-selected={game === item} className={game === item ? 'active' : ''} onClick={() => setGame(item)}>
                {item === 'all' ? t('bigWinsGameAll') : t(gameLabelKey(item))}
              </button>
            ))}
          </div>
        ) : null}

        {/* One scrolling row instead of three stacked blocks: the window is the
            only filter worth a permanent tap target, the rest lives in a sheet. */}
        <div className="leaderboard-filterbar">
          <div className="leaderboard-periods" role="tablist" aria-label={t('leaderboardPeriod')}>
            {(board === 'wins' ? WIN_PERIODS : PERIODS).map((item) => (
              <button key={item} type="button" role="tab" aria-selected={period === item} className={period === item ? 'active' : ''} onClick={() => setPeriod(item)}>
                {board === 'wins' ? winPeriodLabel(item, t) : periodLabel(item, t)}
              </button>
            ))}
          </div>
          <span className="leaderboard-filterbar-sep" aria-hidden="true" />
          {board !== 'wins' ? (
            <>
              {board === 'profit' ? (
                <button
                  type="button"
                  className={`leaderboard-online-toggle ${onlineOnly ? 'active' : ''}`}
                  aria-pressed={onlineOnly}
                  onClick={() => setOnlineOnly((value) => !value)}
                >
                  <em className="leaderboard-live-dot" aria-hidden="true" />
                  {t('leaderboardOnlineOnly')}
                </button>
              ) : null}
              <button type="button" className={`leaderboard-filters-btn ${activeFilterCount ? 'active' : ''}`} onClick={() => setFiltersOpen(true)}>
                <SlidersHorizontal size={13} aria-hidden="true" />
                {t('leaderboardFilters')}
                {activeFilterCount ? <b>{activeFilterCount}</b> : null}
              </button>
            </>
          ) : null}
        </div>

        {roomsQuery.isError ? (
          <p className="leaderboard-room-state error" data-leaderboard-room-state-react="true">
            {t('leaderboardRoomsLoadError')}
            <button type="button" onClick={() => roomsQuery.refetch()}>{t('retry')}</button>
          </p>
        ) : null}
      </section>

      <section className="leaderboard-panel">
        {leaderboardQuery.isLoading ? <LeaderboardState title={t('leaderboardLoadingTitle')} /> : null}
        {leaderboardQuery.isError ? (
          <LeaderboardState
            icon="error"
            title={t('leaderboardLoadErrorTitle')}
            body={t('leaderboardLoadErrorBody')}
            actionLabel={t('retry')}
            onAction={() => leaderboardQuery.refetch()}
          />
        ) : null}
        {empty ? (
          <LeaderboardState
            icon="empty"
            title={board === 'wins'
              ? t('bigWinsEmptyTitle')
              : nobodyInProfit
                ? t('leaderboardEmptyNoProfitTitle')
                : onlineOnly ? t('leaderboardEmptyOnlineTitle') : t('leaderboardEmptyTitle')}
            // An empty board must say what hid the rows, or it just looks broken.
            body={board === 'wins'
              ? t('bigWinsEmptyBody')
              : nobodyInProfit
                ? t('leaderboardEmptyNoProfitBody')
                : onlineOnly
                  ? t('leaderboardEmptyOnlineBody')
                  : appliedMinPlays > 1
                    ? t('leaderboardMinPlaysNote').replace('%n%', String(appliedMinPlays))
                    : t('leaderboardEmptyBody')}
            actionLabel={board === 'wins' && period !== '30d'
              ? t('bigWinsSeeLonger')
              : nobodyInProfit && nextPeriod
                ? t('leaderboardSeeLonger')
                : onlineOnly ? t('leaderboardShowAll') : undefined}
            onAction={board === 'wins' && period !== '30d'
              ? () => setPeriod('30d')
              : nobodyInProfit && nextPeriod
                ? () => setPeriod(nextPeriod)
                : onlineOnly ? () => setOnlineOnly(false) : undefined}
          />
        ) : null}
        {rows.length > 0 ? (
          board === 'wins' ? (
            <>
              {leaderboardQuery.data?.lastJackpot ? (
                <LastJackpotCard
                  row={leaderboardQuery.data.lastJackpot}
                  nowSeconds={nowSeconds}
                  onOpen={() => openJackpot(leaderboardQuery.data?.lastJackpot)}
                />
              ) : null}
              <BigWinsSummary
                count={leaderboardQuery.data?.summary?.count || 0}
                total={Number(leaderboardQuery.data?.summary?.totalPayout || 0)}
              />
              {/* 「今天/更早」的分界线由这一个值定，不让每行各读一次挂钟：
                  跨零点那一刻，先渲染的行会算成「昨天」、后渲染的算成「今天」。
                  列表本来就跟着 30s 轮询重渲染，所以这个值不会陈旧。 */}
              <div className="leaderboard-list">
                {rows.map((row, index) => (
                  <BigWinRow
                    key={`${row.game}-${row.roundNumber}-${row.user?.id}-${index}`}
                    item={row}
                    rank={index + 1}
                    nowSeconds={nowSeconds}
                    onOpen={() => openWin(row)}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {showPodium ? (
                <div className="leaderboard-podium">
                  {podium.map((item) => <PodiumCard key={item.rank} item={item} onOpen={openProfile} />)}
                </div>
              ) : board === 'profit' && effectiveSort === 'profit' ? (
                <p className="leaderboard-note">{t('leaderboardNoWinnersNote')}</p>
              ) : null}
              <div className="leaderboard-list">
                {rest.map((item) => (
                  <LeaderboardRow
                    key={`${item.rank}:${item.user.id}`}
                    item={item}
                    board={board}
                    sort={effectiveSort}
                    onOpen={openProfile}
                  />
                ))}
              </div>
              {board === 'profit' && appliedMinPlays > 1 ? (
                <p className="leaderboard-note">{t('leaderboardMinPlaysNote').replace('%n%', String(appliedMinPlays))}</p>
              ) : null}
            </>
          )
        ) : null}
      </section>

      {/* Your own standing, always in reach — the board is 50 rows deep and a
          player outside them used to have no way to see where they stand. */}
      {myRow && board !== 'wins' ? <MyRankBar item={myRow} board={board} sort={effectiveSort} minPlays={appliedMinPlays} onOpen={openProfile} /> : null}

      <SheetPortal>
        {filtersOpen ? (
          <FiltersSheet
            game={game}
            roomId={roomId}
            sort={sort}
            minPlays={minPlays}
            board={board}
            roomOptions={roomOptions}
            roomsLoading={roomsQuery.isLoading}
            onGame={changeGame}
            onRoom={setRoomId}
            onSort={setSort}
            onMinPlays={setMinPlays}
            onClose={() => setFiltersOpen(false)}
          />
        ) : null}
      </SheetPortal>

      {/* At <body>, out of this scrolling page — see SheetPortal. */}
      <SheetPortal>
        {profileUser ? (
          <PublicProfileSheet
            user={profileUser}
            boardPeriod={period}
            roomNames={roomNames}
            onClose={closeProfile}
          />
        ) : null}
      </SheetPortal>

      {/* At <body>, out of this scrolling page — see SheetPortal. Without this,
          Mobile Safari scopes the sheet's `position: fixed` to `.tab-page`'s
          scroller and the tab bar paints over its bottom buttons. */}
      <SheetPortal>
        {winRound ? (
          <RoundDetailSheet
            round={winRound.round}
            tier={winRound.tier as MilharTierId}
            currency={country.fiatCurrency}
            token={token}
            onClose={() => setWinRound(null)}
          />
        ) : null}
      </SheetPortal>

    </section>
  );
}


// Three cards that must read as one row. The room chip only appeared on players
// who happened to be online, so it made the podium ragged — a card with it stood
// taller than its neighbours, and long room names ("Sic Bo · Sala VIP") wrapped
// to two lines and pushed it taller still, which then squeezed the profit above
// it into an ellipsis. The country was decoration on every card alike. Both are
// on the profile, one tap away; the medal, the name and the number are the
// podium's whole job.
function PodiumCard({ item, onOpen }: {
  item: UiLeaderboardItem;
  onOpen: (item: UiLeaderboardItem) => void;
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className={`podium-card rank-${item.rank} ${item.isMe ? 'mine' : ''}`}>
      <button className="podium-open" type="button" onClick={() => onOpen(item)}>
        <span className="podium-medal">{medalFor(item.rank)}</span>
        <Avatar item={item} size="large" />
        <strong>{item.user.username}</strong>
        {/* Whole reais: "+R$ 2.567,50" ran past the card and the leader's
            figure — the one number this block exists for — showed as "+R$ 2.567…". */}
        <b className={profitTone(item.profit)}>{fmtSignedBRL(Math.round(item.profit))}</b>
        {item.isMe ? <em>{t('chipYou')}</em> : null}
      </button>
    </div>
  );
}

function LeaderboardRow({ item, board, sort, onOpen }: {
  item: UiLeaderboardItem;
  board: LeaderboardBoard;
  sort: LeaderboardSort;
  onOpen: (item: UiLeaderboardItem) => void;
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className={`leaderboard-row ${item.isMe ? 'mine' : ''}`}>
      <button className="leaderboard-row-open" type="button" onClick={() => onOpen(item)}>
        <span className="leaderboard-rank">{item.rank}</span>
        <Avatar item={item} />
        {/* Name and rank only. The country sat inside the name ("rafapires BR")
            where it read as part of the handle, and the subline spent a second
            line on "5 plays · 60% win · Last Digit · …" that the width could
            not hold — it truncated mid-room-name on every row. Both belong to
            the profile, which is one tap away. */}
        <div className="leaderboard-row-main">
          <strong>
            {item.user.username}
            {item.isMe ? <em>{t('chipYou')}</em> : null}
          </strong>
        </div>
        <MetricValue item={item} board={board} sort={sort} />
      </button>
    </div>
  );
}

// The number on the right is whatever the board is ranked by — showing profit
// while sorting by ROI is how a list stops making sense.
function MetricValue({ item, board, sort }: { item: UiLeaderboardItem; board: LeaderboardBoard; sort: LeaderboardSort }) {
  const t = useI18nStore((state) => state.t);
  if (sort === 'roi') {
    return (
      <b className={`leaderboard-metric ${profitTone(item.roi)}`}>
        {fmtSignedPct(item.roi)}
        <small>{fmtSignedBRL(item.profit)}</small>
      </b>
    );
  }
  if (sort === 'streak') {
    return (
      <b className={`leaderboard-metric ${item.streak > 0 ? 'tone-green' : 'tone-mute'}`}>
        {item.streak > 0 ? `${fmtInt(item.streak)}🔥` : '—'}
        <small>{fmtSignedBRL(item.profit)}</small>
      </b>
    );
  }
  return <b className={`leaderboard-metric ${profitTone(item.profit)}`}>{fmtSignedBRL(item.profit)}</b>;
}

/**
 * 一次中奖。
 *
 * <p>密度是这个榜的第一属性（设计稿 §5.1）：一屏 3 条读出来是「很罕见」，
 * 一屏 12 条才是「一直在发生」。所以「买了几期」这类深度信息不在行上 ——
 * 它在点进去的开奖详情里。
 */
function BigWinRow(
  { item, rank, nowSeconds, onOpen }:
  { item: UiLeaderboardItem; rank: number; nowSeconds: number; onOpen: () => void }
) {
  const t = useI18nStore((state) => state.t);
  const mult = multipleLabel(item.multiple);
  const stake = Number(item.stake || 0);
  return (
    <button
      className={`bigwin-row${item.isMe ? ' is-me' : ''}${item.isJackpot ? ' is-jackpot' : ''}`}
      type="button"
      onClick={onOpen}
    >
      <span className={`bigwin-rank r${rank <= 3 ? rank : 0}`}>{rank}</span>
      <Avatar item={item} size="normal" />
      <span className="bigwin-main">
        <span className="bigwin-name">
          {item.user?.username || '--'}
          {item.isMe ? <em>{t('chipYou')}</em> : null}
        </span>
        <span className="bigwin-game">
          {/* 头奖徽章。大厅天天挂着「N 天没出头奖 · 奖池 R$ X」，而真出了头奖的
              那一次，榜上以前和一笔 ×150 长得一模一样 —— 唯一的线索是倍数是个
              奇怪的小数（×2734,8）。这个榜最该喊出来的一件事，以前是静音的。 */}
          {item.isJackpot ? <em className="bigwin-jackpot">{t('bigWinsJackpot')}</em> : null}
          {t(gameLabelKey(item.game || ''))} {bigWinWhenLabel(item, nowSeconds)}
        </span>
      </span>
      <span className="bigwin-value">
        <b>{fmtMoney(item.payout)}</b>
        {/* "from R$ 5" — the same R$ 2 → R$ 5.469 story the jackpot card tells.
            A bare ×2.000 read as a count to most players; the stake says what
            the win cost, which is the question a bystander is actually asking. */}
        <small>{stake > 0 ? t('bigWinsFromStake').replace('%v%', fmtMoney(stake)) : (mult || '')}</small>
      </span>
      <ChevronRight size={14} aria-hidden="true" className="bigwin-cv" />
    </button>
  );
}

/**
 * 头奖卡：上一次真的有人把累积奖池拿走，是什么时候、谁、花了多少本钱。
 *
 * <p>为什么它排在榜单之前、而且不受时间窗口限制：**头奖大约一个月才一次**，
 * 30 天窗口里经常一条都没有。而大厅天天挂着「N 天没出头奖 · 奖池 R$ 84.585」——
 * 那句话在产品里一直是孤证，玩家只看到一个越来越大的数字，没有任何东西证明这钱
 * 真的会付出去。这张卡就是那个证明（BIG_WINS_BOARD_DESIGN §5.6）。
 *
 * <p>「N 天前」和奖池数字是互相印证的一对：越久没人中，那个数字越大。稀缺在这里
 * 不是缺点，是张力。
 *
 * <p>`user` 为 null = 中奖者关掉了公开榜。**这一笔仍然要显示，只是不出名字** ——
 * 整行滤掉会让卡片说「上一次头奖是 60 天前」，而事实是昨天刚出过一次。
 */
function LastJackpotCard(
  { row, nowSeconds, onOpen }:
  { row: NonNullable<LeaderboardResponse['lastJackpot']>; nowSeconds: number; onOpen: () => void }
) {
  const t = useI18nStore((state) => state.t);
  const days = daysAgo(row.wonAt, nowSeconds);
  const stake = Number(row.stake || 0);
  const when = days === null ? '' : days === 0 ? t('bigWinsJackpotToday')
    : days === 1 ? t('bigWinsJackpotYesterday')
      : t('bigWinsJackpotDaysAgo').replace('%n%', String(days));

  return (
    <button className="jackpot-card" type="button" onClick={onOpen}>
      <span className="jackpot-card-head">
        <em>{t('bigWinsJackpot')}</em>
        <span>{t('bigWinsLastJackpot')}</span>
      </span>
      {/* 本金 → 派彩。「R$ 2 变成 R$ 5.469」是这张卡唯一要让人记住的一句话，
          它同时回答了「我也行吗」—— 中头奖的人买的不是大票。 */}
      <span className="jackpot-card-money">
        {stake > 0 ? <i>{fmtMoney(stake)}</i> : null}
        {stake > 0 ? <s>→</s> : null}
        <b>{fmtMoney(Number(row.payout || 0))}</b>
      </span>
      <span className="jackpot-card-who">
        {row.user?.username ? <strong>{row.user.username}</strong> : null}
        <span>
          {t(gameLabelKey(row.game || ''))}
          {when ? ` · ${when}` : ''}
        </span>
      </span>
      <ChevronRight size={14} aria-hidden="true" className="jackpot-card-cv" />
    </button>
  );
}

function BigWinsSummary({ count, total }: { count: number; total: number }) {
  const t = useI18nStore((state) => state.t);
  if (count <= 0) return null;
  return (
    <div className="bigwin-summary">
      <b>{fmtInt(count)}</b>
      <span>{t('bigWinsSummary').replace('%n%', '').trim()}</span>
      <em>{fmtMoney(total)}</em>
    </div>
  );
}

function MyRankBar({ item, board, sort, minPlays, onOpen }: {
  item: UiLeaderboardItem;
  board: LeaderboardBoard;
  sort: LeaderboardSort;
  minPlays: number;
  onOpen: (item: UiLeaderboardItem) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const missing = Math.max(0, minPlays - item.plays);
  return (
    <button className="leaderboard-myrank" type="button" onClick={() => onOpen(item)}>
      <span className="leaderboard-rank">{item.ranked && item.rank > 0 ? `#${item.rank}` : '—'}</span>
      <Avatar item={item} />
      <div className="leaderboard-row-main">
        <strong>{t('chipYou')} · {item.user.username}</strong>
        <small>
          {item.ranked
            ? `${playsLabel(item.plays, t)} · ${Math.round(item.winRate * 100)}% ${t('statWin')}`
            : missing > 0
              ? t('leaderboardMyRankNeed').replace('%n%', String(missing))
              : t('leaderboardMyRankUnranked')}
        </small>
      </div>
      <MetricValue item={item} board={board} sort={sort} />
    </button>
  );
}

function FiltersSheet({ game, roomId, sort, minPlays, board, roomOptions, roomsLoading, onGame, onRoom, onSort, onMinPlays, onClose }: {
  game: LeaderboardGame;
  roomId: string;
  sort: LeaderboardSort;
  minPlays: number;
  board: LeaderboardBoard;
  roomOptions: Room[];
  roomsLoading: boolean;
  onGame: (game: LeaderboardGame) => void;
  onRoom: (roomId: string) => void;
  onSort: (sort: LeaderboardSort) => void;
  onMinPlays: (minPlays: number) => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [roomsOpen, setRoomsOpen] = useState(roomId !== 'all');
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet leaderboard-filters-sheet" role="dialog" aria-modal="true" aria-label={t('leaderboardFilters')} onMouseDown={(event) => event.stopPropagation()} data-leaderboard-filters-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div><h2>{t('leaderboardFilters')}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="leaderboard-filters-body">
          {board === 'profit' ? (
            <>
              <span className="profile-section-label">{t('leaderboardSortLabel')}</span>
              <div className="leaderboard-chip-row">
                {SORTS.map((item) => (
                  <button key={item} type="button" className={sort === item ? 'active' : ''} onClick={() => onSort(item)}>
                    {sortLabel(item, t)}
                  </button>
                ))}
              </div>
            </>
          ) : null}

          <span className="profile-section-label">{t('leaderboardGameFilter')}</span>
          <div className="leaderboard-chip-row">
            {GAMES.map((item) => (
              <button key={item} type="button" className={game === item ? 'active' : ''} onClick={() => onGame(item)}>
                {gameLabel(item, t)}
              </button>
            ))}
          </div>

          {/* Ten room chips is most of the sheet, and almost nobody filters by
              room. Folded behind its label unless a room is already picked. */}
          <button type="button" className="leaderboard-rooms-toggle" aria-expanded={roomsOpen} onClick={() => setRoomsOpen((v) => !v)}>
            <span className="profile-section-label">{t('leaderboardRoomFilter')}</span>
            <span>{(() => { const picked = roomOptions.find((r) => r.id === roomId); return picked ? roomLabel(picked, t) : t('leaderboardAllRooms'); })()}</span>
            <ChevronRight size={14} aria-hidden="true" className={roomsOpen ? 'is-open' : ''} />
          </button>
          {roomsOpen ? (
            <div className="leaderboard-chip-row">
              <button type="button" className={roomId === 'all' ? 'active' : ''} onClick={() => onRoom('all')}>
                {t('leaderboardAllRooms')}
              </button>
              {roomOptions.map((room) => (
                <button key={room.id} type="button" className={roomId === room.id ? 'active' : ''} onClick={() => onRoom(room.id)}>
                  {roomLabel(room, t)}
                </button>
              ))}
            </div>
          ) : null}
          {roomsOpen && roomsLoading ? <p className="leaderboard-room-state">{t('leaderboardRoomsLoading')}</p> : null}

          {board === 'profit' ? (
            <>
              <span className="profile-section-label">{t('leaderboardMinPlays')}</span>
              <div className="leaderboard-chip-row">
                {MIN_PLAYS_OPTIONS.map((value) => (
                  <button key={value} type="button" className={minPlays === value ? 'active' : ''} onClick={() => onMinPlays(value)}>
                    {value}+
                  </button>
                ))}
              </div>
              <p className="leaderboard-note">{t('leaderboardMinPlaysHint')}</p>
            </>
          ) : null}
        </div>

        <button className="leaderboard-filters-done" type="button" onClick={onClose}>{t('leaderboardFiltersDone')}</button>
      </section>
    </div>
  );
}

function Avatar({ item, size = 'normal' }: { item: UiLeaderboardItem; size?: 'normal' | 'large' }) {
  return (
    <span className={`leaderboard-avatar ${size} ${item.online ? 'is-online' : ''}`}>
      {item.user.avatarUrl ? <img src={item.user.avatarUrl} alt="" /> : item.user.initial || item.user.username[0] || '?'}
    </span>
  );
}


function LeaderboardState({ title, body, icon, actionLabel, onAction }: { title: string; body?: string; icon?: 'error' | 'empty'; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="leaderboard-state">
      {icon === 'error' ? <AlertTriangle size={30} aria-hidden="true" /> : icon === 'empty' ? <Trophy size={30} aria-hidden="true" /> : <Medal size={30} aria-hidden="true" />}
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

function normalizeLeaderboard(items: LeaderboardItem[]): UiLeaderboardItem[] {
  return items.map((item, index) => {
    const user = item.user || {};
    const username = user.username || 'jogador';
    return {
      rank: Number(item.rank || index + 1),
      // Older payloads have no `ranked` flag; every row they carried was ranked.
      ranked: item.ranked === undefined ? true : !!item.ranked,
      user: {
        id: String(user.id ?? user.user_id ?? user.userId ?? user.player_id ?? user.playerId ?? index + 1),
        username,
        initial: String(user.initial || username[0] || '?').toUpperCase(),
        flag: user.flag || 'BR',
        level: Number(user.vipLevel ?? user.level ?? 1) || 1,
        avatarUrl: leaderboardAvatarUrl(user),
        publicLeaderboard: user.publicLeaderboard ?? user.public_leaderboard,
        isAdmin: !!(user.isAdmin || user.is_admin),
      },
      plays: Number(item.plays || 0),
      wins: Number(item.wins || 0),
      winRate: Number(item.winRate || 0),
      // No clamping: a negative hour is the truth and the board now says so.
      profit: Number(item.profit || 0),
      roi: Number(item.roi || 0),
      streak: Number(item.streak || 0),
      online: !!item.online,
      roomCode: String(item.roomCode || ''),
      lastBet: item.lastBet || null,
      copyRoomCode: String(item.copyRoomCode || ''),
      game: String(item.game || ''),
      tier: item.tier == null ? null : String(item.tier),
      roundNumber: typeof item.roundNumber === 'number' ? item.roundNumber : null,
      roundLabel: String(item.roundLabel || ''),
      isJackpot: item.isJackpot === true,
      roundStartsAt: typeof item.roundStartsAt === 'number' ? item.roundStartsAt : null,
      eventId: item.eventId == null ? null : String(item.eventId),
      payout: Number(item.payout || 0),
      // `|| 0` is wrong on these two: Draw's null would become 0, and the
      // screen would show "R$ 0" and "×0" — a plausible-looking lie.
      stake: item.stake == null ? null : Number(item.stake),
      multiple: item.multiple == null ? null : Number(item.multiple),
      wonAt: Number(item.wonAt || 0),
      isMe: !!item.isMe,
    };
  });
}

function roomLabelMap(rooms: Room[], t: ReturnType<typeof useI18nStore.getState>['t']): RoomNames {
  const out: RoomNames = {};
  for (const room of rooms) {
    const label = roomLabel(room, t);
    if (room.code) out[room.code] = label;
    if (room.id) out[room.id] = label;
  }
  return out;
}

function profileRouteId(item: UiLeaderboardItem) {
  return profileApiUserId(item.user) || String(item.user.id || item.user.username || '').trim();
}

function findLeaderboardProfile(rows: UiLeaderboardItem[], routeId: string) {
  const normalized = String(routeId || '').trim();
  if (!normalized) return null;
  return rows.find((item) => (
    profileRouteId(item) === normalized ||
    String(item.user.id || '').trim() === normalized ||
    String(item.user.username || '').trim().toLowerCase() === normalized.toLowerCase()
  )) || null;
}

function routeProfileFallback(routeId: string): UiLeaderboardItem {
  const value = String(routeId || '').trim();
  const username = /^[a-z][a-z0-9_.-]{2,}$/i.test(value) ? value : 'jogador';
  return {
    rank: 0,
    ranked: false,
    user: {
      id: value,
      username,
      initial: String(username[0] || '?').toUpperCase(),
      flag: 'BR',
      level: 1,
      isAdmin: false,
    },
    plays: 0,
    wins: 0,
    winRate: 0,
    profit: 0,
    roi: 0,
    streak: 0,
    online: false,
    roomCode: '',
    lastBet: null,
    copyRoomCode: '',
    game: '', tier: null, roundNumber: null, roundLabel: '', roundStartsAt: null, eventId: null,
    isJackpot: false,
    payout: 0, stake: null, multiple: null, wonAt: 0,
    isMe: false,
  };
}

function sortLabel(sort: LeaderboardSort, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (sort === 'roi') return t('leaderboardSortRoi');
  if (sort === 'streak') return t('leaderboardSortStreak');
  return t('leaderboardSortProfit');
}

function gameLabel(game: LeaderboardGame, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (game === 'all') return t('leaderboardAllGames');
  return t(gameNameKey(game));
}

function roomLabel(room: Room, t: ReturnType<typeof useI18nStore.getState>['t']) {
  return `${t(gameNameKey(room.game))} · ${room.label || room.id}`;
}

function winPeriodLabel(period: LeaderboardPeriod, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (period === 'today') return t('drawsToday');
  if (period === '7d') return t('bigWinsPeriod7d');
  return t('bigWinsPeriod30d');
}

function medalFor(rank: number) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  return '🥉';
}
